import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  pjNfMensalService,
  type PjNfMensalRow,
  type PjNfParsePreview,
  type PjNfPortalLinkStatus,
} from '@/services/pjNfMensalService'
import { canPortalSubmitNf, currentCompetencia } from '@/components/PjNf/pjNfStatus'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { isPjRole } from '@/config/routeAccess'
import { useAuthStore } from '@/stores/authStore'

export function usePortalPjNfPage(enabled: boolean) {
  const role = useAuthStore((s) => s.user?.role) ?? ''
  const isPjOnly = isPjRole(role)
  const [linkStatus, setLinkStatus] = useState<PjNfPortalLinkStatus | null>(null)
  const [rows, setRows] = useState<PjNfMensalRow[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [uploadRowId, setUploadRowId] = useState<string | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [parsePreview, setParsePreview] = useState<PjNfParsePreview | null>(null)
  const [uploadedMeta, setUploadedMeta] = useState<{
    fileUrl: string
    fileName: string
    mimeType: string
  } | null>(null)

  const load = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    try {
      const status = await pjNfMensalService.getPortalLinkStatus()
      setLinkStatus(status)
      if (status.linked) {
        const mine = await pjNfMensalService.listMine()
        setRows(mine.data)
      } else {
        setRows([])
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 403) {
        setLinkStatus({ linked: false })
        setRows([])
        return
      }
      toast.error(getFriendlyError(err, 'Erro ao carregar notas fiscais'))
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    void load()
  }, [load])

  const competenciaAtual = currentCompetencia()
  const currentRow = rows.find((r) => r.competencia === competenciaAtual) ?? null

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => b.competencia.localeCompare(a.competencia)),
    [rows],
  )

  const uploadRow = uploadRowId ? rows.find((r) => r.id === uploadRowId) ?? null : null

  const canSubmit = Boolean(
    uploadRow &&
      uploadedMeta &&
      file &&
      !parsing &&
      !submitting &&
      parsePreview &&
      (parsePreview.valid || parsePreview.canSubmitWithRessalvas),
  )

  const needsRessalvaConfirm =
    Boolean(parsePreview?.canSubmitWithRessalvas && !parsePreview.valid && parsePreview.ressalvas.length > 0)

  const resetUploadForm = useCallback(() => {
    setFile(null)
    setParsePreview(null)
    setUploadedMeta(null)
  }, [])

  const openUpload = useCallback(
    (row: PjNfMensalRow) => {
      if (!canPortalSubmitNf(row.status)) return
      setUploadRowId(row.id)
      resetUploadForm()
    },
    [resetUploadForm],
  )

  const closeUpload = useCallback(() => {
    setUploadRowId(null)
    resetUploadForm()
  }, [resetUploadForm])

  const handleFileChange = useCallback(
    async (nextFile: File | null) => {
      setFile(nextFile)
      setParsePreview(null)
      setUploadedMeta(null)
      if (!nextFile || !uploadRow) return

      setParsing(true)
      try {
        const [preview, uploaded] = await Promise.all([
          pjNfMensalService.parseNf(nextFile, uploadRow.competencia),
          pjNfMensalService.uploadAttachment(nextFile),
        ])
        setParsePreview({
          ...preview,
          ressalvas: preview.ressalvas ?? [],
          errors: preview.errors ?? [],
          warnings: preview.warnings ?? [],
          canSubmitWithRessalvas: preview.canSubmitWithRessalvas ?? false,
        })
        setUploadedMeta({
          fileUrl: uploaded.fileUrl,
          fileName: uploaded.fileName,
          mimeType: uploaded.mimeType,
        })
      } catch (err) {
        toast.error(getFriendlyError(err, 'Erro ao ler arquivo da NF'))
      } finally {
        setParsing(false)
      }
    },
    [uploadRow],
  )

  const submit = useCallback(async () => {
    if (!uploadRow || !uploadedMeta || !parsePreview) return false

    if (needsRessalvaConfirm) {
      const message = [
        'Foram encontradas divergências nesta NF:',
        ...parsePreview.ressalvas.map((item) => `• ${item}`),
        '',
        'O financeiro (Janice) será alertado. Deseja enviar mesmo assim?',
      ].join('\n')
      if (!window.confirm(message)) return false
    }

    setSubmitting(true)
    try {
      const hasRessalvas =
        parsePreview.ressalvas.length > 0 &&
        !parsePreview.valid &&
        parsePreview.canSubmitWithRessalvas

      const body = {
        nfFileUrl: uploadedMeta.fileUrl,
        nfFileName: uploadedMeta.fileName,
        nfMimeType: uploadedMeta.mimeType,
        ...(hasRessalvas ? { confirmarRessalvas: true } : {}),
      }
      if (uploadRow.status === 'REJEITADA') {
        await pjNfMensalService.resubmit(uploadRow.id, body)
      } else {
        await pjNfMensalService.submit(uploadRow.id, body)
      }
      toast.success(`NF de ${uploadRow.competencia} enviada ao financeiro`)
      closeUpload()
      await load()
      return true
    } catch (err) {
      toast.error(getFriendlyError(err, 'Erro ao enviar NF'))
      return false
    } finally {
      setSubmitting(false)
    }
  }, [closeUpload, load, needsRessalvaConfirm, parsePreview, uploadRow, uploadedMeta])

  return {
    linkStatus,
    rows: sortedRows,
    loading,
    isPjOnly,
    competenciaAtual,
    currentRow,
    uploadRowId,
    uploadRow,
    openUpload,
    closeUpload,
    file,
    handleFileChange,
    parsePreview,
    parsing,
    canSubmit,
    needsRessalvaConfirm,
    submitting,
    submit,
    reload: load,
  }
}
